/**
 * Tour Guide — the module's entire public surface.
 *
 * ┌─ INTEGRATING THIS MODULE ─────────────────────────────────────────┐
 * │ 1. Mount `<TourGuide />` once, anywhere inside the app shell.      │
 * │ 2. Put `data-tour-slot="header-actions"` on the element the        │
 * │    launcher should appear in.                                      │
 * │                                                                    │
 * │ That is the whole integration. No page, no route and no navigation │
 * │ component imports anything from here, and none of them needs a     │
 * │ tour attribute — steps find their targets through the resolver in  │
 * │ `engine/anchors.ts`.                                               │
 * │                                                                    │
 * │ To remove the feature entirely: delete this folder and those two   │
 * │ things. Nothing else in the codebase references it.                │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * Editing the tour itself means editing `tours.ts` and nothing else.
 */

import React from 'react';

import './tour-guide.css';
import { TourGuideProvider, type TourGuideProviderProps } from './TourGuideProvider';
import { ActionDock } from './ui/ActionDock';
import { DemoBookingButton } from './ui/DemoBookingButton';
import { PricingButton } from './ui/PricingButton';
import { TaskFlowTracker } from './ui/TaskFlowTracker';
import { TaskSpotlight } from './ui/TaskSpotlight';
import { TourLauncher } from './ui/TourLauncher';
import { TourTracker } from './ui/TourTracker';

export interface TourGuideProps extends TourGuideProviderProps {
  /** Render the header launcher. Off for a host that wants its own trigger. */
  withLauncher?: boolean;
  /** Render the live stage tracker while a tour runs. */
  withTracker?: boolean;
  /**
   * Render the "Demo" button next to the launcher.
   *
   * 🔴 DEFAULTS TO FALSE, unlike the tour itself. Booking a demo and comparing
   * plans are things you offer a PROSPECT evaluating the product in the sandbox;
   * showing them to a paying customer inside their own workspace is at best noise
   * and at worst insulting. Defaulting off means a host that mounts `<TourGuide/>`
   * without thinking gets the guided tour and no sales buttons — the sandbox opts
   * in explicitly, so the intent is visible where the sandbox is known about.
   */
  withDemoBooking?: boolean;
  /** Render the "Plans" link to the pricing page. Off by default — see above. */
  withPricing?: boolean;
  /**
   * Offer the guided creation flows — the "build one yourself" walkthroughs that
   * hang off individual tour stops.
   *
   * Off by default, and mapped to the provider's `tasks`. A flow CREATES a record;
   * see `TourGuideProviderProps.tasks` for why that has to be an explicit, sandbox-
   * aware decision rather than something a host gets by mounting the component.
   */
  withTasks?: boolean;
}

/**
 * The whole feature in one component: state, storage, the launcher and the
 * overlay. Mount it once.
 *
 * It renders `children` untouched, so it can either wrap a subtree or sit as a
 * standalone sibling — whichever fits the host's existing provider stack.
 */
export function TourGuide({
  withLauncher = true,
  withTracker = true,
  withDemoBooking = false,
  withPricing = false,
  withTasks = false,
  children,
  ...providerProps
}: TourGuideProps) {
  const anyButton = withLauncher || withDemoBooking || withPricing;

  return (
    <TourGuideProvider {...providerProps} tasks={withTasks}>
      {anyButton && (
        /*
          The dock decides WHERE these live: the header on a wide screen, a
          bottom-right widget below `lg` where the header row cannot hold them.
          It owns the HeaderSlot portal, so this stays one mount point.
        */
        <ActionDock>
          {withLauncher && <TourLauncher />}
          {withPricing && <PricingButton />}
          {withDemoBooking && <DemoBookingButton />}
        </ActionDock>
      )}
      {withTracker && <TourTracker />}
      {/*
        Both render nothing unless a flow is running, so they are mounted
        unconditionally rather than behind `withTasks`: the flag already gates
        whether a flow can START (the provider hands out no offer without it), and
        gating the chrome as well would mean two switches for one decision.
      */}
      <TaskSpotlight />
      <TaskFlowTracker />
      {children}
    </TourGuideProvider>
  );
}

/* Escape hatches for hosts that want to compose the pieces themselves. */
export { TourGuideProvider, useTourGuide, type TourGuideApi } from './TourGuideProvider';
export { TourLauncher } from './ui/TourLauncher';
export { TourTracker } from './ui/TourTracker';
export { HeaderSlot } from './ui/HeaderSlot';
export { ActionDock } from './ui/ActionDock';
export { DemoBookingButton } from './ui/DemoBookingButton';
export { DemoBookingDialog } from './ui/DemoBookingDialog';
export { PricingButton } from './ui/PricingButton';
export { TaskSpotlight } from './ui/TaskSpotlight';
export { TaskFlowTracker } from './ui/TaskFlowTracker';
export {
  TASKS,
  getTask,
  getTaskForStep,
  validateTasks,
  type TaskFlow,
  type TaskStep,
} from './tasks';
export {
  DEMO_OPTIONS,
  PLANS_URL,
  validateSandboxLinks,
  type DemoOption,
} from './sandboxLinks';
export { PRODUCT_TOUR, TOURS, computeStageProgress, getTour, validateTours } from './tours';
export type {
  Tour,
  TourStep,
  TourStage,
  StageProgress,
  StepProgress,
  TourProgressView,
} from './tours';
export {
  createApiProgressStore,
  createLocalProgressStore,
  createMemoryProgressStore,
  type TourProgressStore,
} from './engine/progressStore';
export {
  HOST,
  resolveAnchor,
  sidebarAnchor,
  sidebarCardAnchor,
  submenuAnchor,
  type AnchorSpec,
} from './engine/anchors';

export default TourGuide;
